import * as pc from 'playcanvas'
import type {
  Interaction,
  NormalizedRun,
  ProjectedAgent,
  TranscriptEvent,
} from '../../api/types'
import { useArenaStore } from '../../store'
import { computeAvatarState, type VoxelAvatarState } from '../avatarState'
import {
  IDLE_VARIATION_PERIOD_S,
  SYNTY_ROSTER_SLOTS,
  phaseOffsetSeconds,
  pickVariant,
  spawnCharacter,
  spawnSyntyRosterCharacter,
  syntyIdleClipFor,
  type CharacterHandle,
  type CharacterVisualStatus,
  type HumanVariant,
} from '../characters'
import {
  getColliders,
  hasClearSightline,
  type AABB,
} from '../colliders'
import { computeLabelScreenPos } from './worldProjection'
import {
  rosterPlacement,
  rosterWorldPosition,
  ROSTER_CAPACITY,
  type RosterPlacement,
} from './floorPrograms'
import {
  createWorldOverlay,
  type OverlayHandle,
} from './interiorShared'
import { worldRoot } from './worldRoot'
import {
  bindStationVisualMaterials,
  createStationVisualMaterials,
  spawnStationVisual,
  type StationVisual,
  type StationVisualMaterials,
} from './stationVisual'
import type { SharedMonitorDisplay } from './monitorDisplay'

export interface RosterAgent {
  id: string
  agentId: string
  name: string
  room: string
  worldPos: [number, number, number]
  boundRunId?: string
}

export interface RosterCharacterPlan {
  source: 'synty-roster' | 'known-good'
  fallbackVariant: HumanVariant
}

/** Arena-owned visual policy; Portal projection identity is not consulted. */
export function rosterCharacterPlan(agentId: string, index: number): RosterCharacterPlan {
  return {
    source: index < SYNTY_ROSTER_SLOTS ? 'synty-roster' : 'known-good',
    fallbackVariant: pickVariant(agentId),
  }
}

export interface SyntyRosterDiagnostic {
  agentId: string
  placement: RosterPlacement
  expectedIdleClip: string
  phaseSeconds: number
  visual: CharacterVisualStatus
}

export interface AgentRosterLifecycleDiagnostic {
  roomActive: boolean
  generation: number
  entryCount: number
  activeFloorY: number | null
  activeCharacterCount: number
  pausedCharacterCount: number
}

export interface AgentRunBindings {
  byAgentId: ReadonlyMap<string, NormalizedRun>
  boundRunIds: ReadonlySet<string>
  unboundRuns: NormalizedRun[]
}

export function placeableRosterAgents(
  agents: readonly ProjectedAgent[],
): ProjectedAgent[] {
  return [...agents]
    .sort((a, b) => a.id.localeCompare(b.id))
    .slice(0, ROSTER_CAPACITY)
}

function key(value: string | undefined): string {
  return value?.trim().toLowerCase() ?? ''
}

function timestamp(value: string): number {
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY
}

/**
 * Bind only explicit Portal agent references. Names are accepted only from the
 * dedicated `agent_name` field and only when unique; run titles are never used.
 */
export function bindRunsToAgents(
  agents: readonly ProjectedAgent[],
  runs: readonly NormalizedRun[],
): AgentRunBindings {
  const byId = new Map(agents.map((agent) => [key(agent.id), agent]))
  const nameCandidates = new Map<string, ProjectedAgent[]>()
  for (const agent of agents) {
    const agentKey = key(agent.name)
    nameCandidates.set(agentKey, [...(nameCandidates.get(agentKey) ?? []), agent])
  }
  const candidates = runs
    .filter((run) => run.status === 'running' && !run.stale)
    .map((run) => {
      const idMatch = byId.get(key(run.agentId))
      const names = nameCandidates.get(key(run.agentName)) ?? []
      return { run, agent: idMatch ?? (names.length === 1 ? names[0] : undefined) }
    })
    .filter((value): value is { run: NormalizedRun; agent: ProjectedAgent } =>
      value.agent !== undefined)
    .sort((a, b) =>
      timestamp(b.run.updatedAt) - timestamp(a.run.updatedAt) ||
      a.run.id.localeCompare(b.run.id))

  const byAgentId = new Map<string, NormalizedRun>()
  const boundRunIds = new Set<string>()
  for (const { agent, run } of candidates) {
    if (byAgentId.has(agent.id)) continue
    byAgentId.set(agent.id, run)
    boundRunIds.add(run.id)
  }
  return {
    byAgentId,
    boundRunIds,
    unboundRuns: runs.filter((run) => !boundRunIds.has(run.id)),
  }
}

interface Entry {
  index: number
  profile: ProjectedAgent
  placement: RosterPlacement
  root: pc.Entity
  materials: StationVisualMaterials
  visual: StationVisual
  status: string
  character: CharacterHandle
  characterActive: boolean
  nameplate: HTMLDivElement
  nameplateStatus: HTMLSpanElement
}

const EMPTY_INTERACTIONS: Interaction[] = []
const EMPTY_TRANSCRIPTS: TranscriptEvent[] = []
/**
 * Nameplates are local spatial affordances, not a global HUD. Keep them near
 * the player and on the player's current HQ floor so desks behind walls or on
 * another floor cannot leak UI into the current view.
 */
export const AGENT_NAMEPLATE_DIST = 12
export const AGENT_NAMEPLATE_FLOOR_TOLERANCE = 2.5
export const AGENT_ROSTER_FLOOR_TOLERANCE = 2.5

/** Select the nearest populated roster floor for one camera height. */
export function rosterFloorForCamera(
  cameraY: number,
  floorYs: readonly number[],
): number | null {
  if (!Number.isFinite(cameraY)) return null
  const floors = [...new Set(floorYs.filter(Number.isFinite))]
  if (floors.length === 0) return null
  const nearest = floors.reduce((current, floorY) =>
    Math.abs(cameraY - floorY) < Math.abs(cameraY - current) ? floorY : current)
  return Math.abs(cameraY - nearest) <= AGENT_ROSTER_FLOOR_TOLERANCE
    ? nearest
    : null
}

export function shouldShowAgentNameplate(
  roomActive: boolean,
  camera: readonly [number, number, number],
  agent: readonly [number, number, number],
  colliders: readonly AABB[] = [],
): boolean {
  if (!roomActive) return false
  if (Math.abs(camera[1] - agent[1]) > AGENT_NAMEPLATE_FLOOR_TOLERANCE) return false
  if (Math.hypot(camera[0] - agent[0], camera[2] - agent[2]) > AGENT_NAMEPLATE_DIST) {
    return false
  }
  return hasClearSightline(
    { x: camera[0], y: camera[1], z: camera[2] },
    { x: agent[0], y: agent[1] + 2.35, z: agent[2] },
    colliders,
  )
}

/** Runtime owner for real projected roster desks, avatars, and run status. */
export class AgentRosterSystem {
  private app!: pc.Application
  private entries = new Map<string, Entry>()
  private roster: RosterAgent[] = []
  private unsubscribe?: () => void
  private overlay: OverlayHandle | null = null
  private overflowBadge: HTMLDivElement | null = null
  private monitorDisplay?: SharedMonitorDisplay
  private sharedMaterials = new Map<string, StationVisualMaterials>()
  private stationBatchGroupId?: number
  private syntyRosterStateOverride: VoxelAvatarState | null = null
  private roomActive = false
  private activeFloorY: number | null = null
  private lifecycleGeneration = 0
  private readonly nameplateProjection = new pc.Vec3()

  init(
    app: pc.Application,
    options: { monitorDisplay?: SharedMonitorDisplay } = {},
  ): void {
    this.app = app
    this.monitorDisplay = options.monitorDisplay
    const batcher = (app as pc.Application & {
      batcher?: Pick<pc.BatchManager, 'addGroup' | 'markGroupDirty' | 'removeGroup'>
    }).batcher
    this.stationBatchGroupId = batcher
      ?.addGroup('agent-roster-stations', true, 64)
      .id
    this.overlay = createWorldOverlay(app, { zIndex: '6' })
    this.overflowBadge = document.createElement('div')
    Object.assign(this.overflowBadge.style, {
      position: 'absolute',
      top: '1rem',
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '0.35rem 0.65rem',
      borderRadius: '999px',
      background: 'rgba(10,14,22,0.9)',
      color: '#fbbf24',
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      display: 'none',
    })
    this.overlay.element.appendChild(this.overflowBadge)
    this.unsubscribe = useArenaStore.subscribe((state, previous) => {
      if (
        state.agents !== previous.agents ||
        state.runs !== previous.runs ||
        state.interactionsByRun !== previous.interactionsByRun ||
        state.transcriptsByRun !== previous.transcriptsByRun
      ) {
        if (this.roomActive) this.sync()
      }
    })
  }

  private materialsFor(status: string): StationVisualMaterials {
    const cached = this.sharedMaterials.get(status)
    if (cached) return cached
    const materials = createStationVisualMaterials(
      status,
      (material) => this.monitorDisplay?.attach(material),
    )
    this.sharedMaterials.set(status, materials)
    return materials
  }

  getRoster(): readonly RosterAgent[] {
    return this.roster
  }

  getLifecycleDiagnostics(): AgentRosterLifecycleDiagnostic {
    const activeCharacterCount = [...this.entries.values()]
      .filter((entry) => entry.characterActive).length
    return {
      roomActive: this.roomActive,
      generation: this.lifecycleGeneration,
      entryCount: this.entries.size,
      activeFloorY: this.activeFloorY,
      activeCharacterCount,
      pausedCharacterCount: this.entries.size - activeCharacterCount,
    }
  }

  getSyntyRosterDiagnostics(): SyntyRosterDiagnostic[] {
    return [...this.entries.values()]
      .filter((entry) => entry.index < SYNTY_ROSTER_SLOTS)
      .map((entry) => ({
        agentId: entry.profile.id,
        placement: entry.placement,
        expectedIdleClip: syntyIdleClipFor(entry.profile.id, 0),
        phaseSeconds: phaseOffsetSeconds(entry.profile.id, IDLE_VARIATION_PERIOD_S),
        visual: entry.character.getVisualStatus?.() ?? {
          requested: pickVariant(entry.profile.id),
          active: pickVariant(entry.profile.id),
          fallback: false,
        },
      }))
  }

  setSyntyRosterState(state: VoxelAvatarState | null): void {
    this.syntyRosterStateOverride = state
    if (state === null) return
    for (const entry of this.entries.values()) {
      if (entry.index < SYNTY_ROSTER_SLOTS) entry.character.setAnimState(state)
    }
  }

  getBoundRunPositions(): Map<string, [number, number, number]> {
    const positions = new Map<string, [number, number, number]>()
    for (const agent of this.roster) {
      if (agent.boundRunId) positions.set(agent.boundRunId, agent.worldPos)
    }
    return positions
  }

  /**
   * Own roster render/update residency at the HQ boundary. Source assets stay
   * in the application-generation cache, so re-entry reuses the same mesh and
   * bank resources while every entity/controller/listener is torn down.
   */
  setRoomActive(active: boolean): void {
    if (active === this.roomActive) return
    this.roomActive = active
    this.lifecycleGeneration += 1
    if (this.overlay) this.overlay.element.style.display = active ? 'block' : 'none'
    if (active) {
      this.sync()
    } else {
      for (const entry of this.entries.values()) entry.nameplate.style.display = 'none'
      this.clear()
    }
  }

  /** Project nearby HQ roster identities after residency has been activated. */
  frame(cameraEntity: pc.Entity): void {
    if (!this.roomActive) return
    const camera = cameraEntity.camera
    if (!camera) return
    const cameraPosition = cameraEntity.getPosition()
    const nextFloorY = rosterFloorForCamera(
      cameraPosition.y,
      [...this.entries.values()].map((entry) => entry.placement.y),
    )
    if (nextFloorY !== this.activeFloorY) {
      this.activeFloorY = nextFloorY
      for (const entry of this.entries.values()) {
        const active = entry.placement.y === nextFloorY
        if (active === entry.characterActive) continue
        entry.character.setActive?.(active)
        entry.characterActive = active
      }
    }
    const width = this.app.graphicsDevice.width
    const height = this.app.graphicsDevice.height
    const viewportMargin = 80
    const colliders = getColliders()
    const projected: Array<{ entry: Entry; x: number; y: number }> = []
    for (const entry of this.entries.values()) {
      const [x, y, z] = rosterWorldPosition(entry.placement)
      if (!shouldShowAgentNameplate(
        true,
        [cameraPosition.x, cameraPosition.y, cameraPosition.z],
        [x, y, z],
        colliders,
      )) {
        entry.nameplate.style.display = 'none'
        continue
      }
      const screen = computeLabelScreenPos(
        x,
        y + 2.35,
        z,
        (world, out) => camera.worldToScreen(world, out),
        this.nameplateProjection,
      )
      if (
        !screen ||
        screen.x < -viewportMargin ||
        screen.x > width + viewportMargin ||
        screen.y < -viewportMargin ||
        screen.y > height + viewportMargin
      ) {
        entry.nameplate.style.display = 'none'
        continue
      }
      projected.push({ entry, x: screen.x, y: screen.y })
    }
    // Parallel desk rows can collapse to the same screen point. Pack labels in
    // deterministic nearby lanes so identity stays legible without changing
    // the authoritative world placement.
    const occupied: Array<{ x: number; y: number }> = []
    const lanes = [0, -1, 1, -2, 2, -3, 3]
    const rows = [0, 1, -1, 2, -2, 3, -3, 4]
    for (const item of projected.sort((a, b) =>
      a.y - b.y || a.x - b.x || a.entry.profile.id.localeCompare(b.entry.profile.id))) {
      let placed: { x: number; y: number } | undefined
      for (const lane of lanes) {
        for (const row of rows) {
          const candidateY = Math.min(height - 6, Math.max(52, item.y + row * 43))
          // The minimap owns the top-right HUD region. Keep world identity
          // labels out of that application-shell surface when it is visible.
          const rightEdge = candidateY < 270
            ? Math.max(66, width - 285)
            : Math.max(66, width - 66)
          const candidate = {
            x: Math.min(rightEdge, Math.max(66, item.x + lane * 128)),
            y: candidateY,
          }
          if (!occupied.some((used) =>
            Math.abs(used.x - candidate.x) < 122 && Math.abs(used.y - candidate.y) < 39)) {
            placed = candidate
            break
          }
        }
        if (placed) break
      }
      if (!placed) {
        item.entry.nameplate.style.display = 'none'
        continue
      }
      occupied.push(placed)
      item.entry.nameplate.style.display = 'block'
      item.entry.nameplate.style.left = `${placed.x}px`
      item.entry.nameplate.style.top = `${placed.y}px`
    }
  }

  private sync(): void {
    if (!this.roomActive) return
    const state = useArenaStore.getState()
    const profiles = placeableRosterAgents(state.agents)
    const overflow = Math.max(0, state.agents.length - profiles.length)
    if (this.overflowBadge) {
      this.overflowBadge.textContent =
        `${overflow} additional project agent${overflow === 1 ? '' : 's'} · roster capacity ${ROSTER_CAPACITY}`
      this.overflowBadge.style.display = overflow > 0 ? 'block' : 'none'
    }
    const identityKey = profiles.map((agent) => `${agent.id}\u0000${agent.name}`).join('\u0001')
    const currentKey = [...this.entries.values()]
      .map((entry) => `${entry.profile.id}\u0000${entry.profile.name}`)
      .join('\u0001')
    if (identityKey !== currentKey) this.rebuild(profiles)

    const bindings = bindRunsToAgents(profiles, state.runs)
    this.roster = profiles.map((profile) => {
      const entry = this.entries.get(profile.id)
      const run = bindings.byAgentId.get(profile.id)
      if (entry) {
        this.applyStatus(entry, run?.status ?? 'queued')
        const projectedState = run
            ? computeAvatarState(
                run,
                state.interactionsByRun[run.id] ?? EMPTY_INTERACTIONS,
                state.transcriptsByRun[run.id] ?? EMPTY_TRANSCRIPTS,
              )
            : 'idle'
        entry.character.setAnimState(
          entry.index < SYNTY_ROSTER_SLOTS && this.syntyRosterStateOverride
            ? this.syntyRosterStateOverride
            : projectedState,
        )
      }
      const placement = entry?.placement ?? rosterPlacement(0)
      return {
        id: `agent:${profile.id}`,
        agentId: profile.id,
        name: profile.name,
        room: placement.room,
        worldPos: rosterWorldPosition(placement),
        ...(run ? { boundRunId: run.id } : {}),
      }
    })
  }

  private rebuild(profiles: readonly ProjectedAgent[]): void {
    if (!this.roomActive) return
    this.clear()
    this.syntyRosterStateOverride = null
    const parent = worldRoot(this.app)
    profiles.forEach((profile, index) => {
      const placement = rosterPlacement(index)
      const root = new pc.Entity(`agent-roster-${profile.id}`)
      root.setLocalPosition(placement.x, placement.y, placement.z)
      root.setLocalEulerAngles(0, placement.rotY, 0)
      parent.addChild(root)
      const materials = this.materialsFor('queued')
      const visual = spawnStationVisual(this.app, root, materials, {
        batchGroupId: this.stationBatchGroupId,
      })

      const characterPlan = rosterCharacterPlan(profile.id, index)
      const character = characterPlan.source === 'synty-roster'
        ? spawnSyntyRosterCharacter(
            this.app,
            [placement.x, placement.y, placement.z],
            profile.id,
            characterPlan.fallbackVariant,
          )
        : spawnCharacter(
            this.app,
            characterPlan.fallbackVariant,
            [placement.x, placement.y, placement.z],
            profile.id,
            {
              ownerTags: [
                'arena-roster-character',
                `arena-roster-agent:${profile.id}`,
              ],
            },
          )
      character.setStation({
        position: [placement.x, placement.y, placement.z],
        rotY: placement.rotY,
      })
      // Camera ownership is established by frame(). Start suspended so a new
      // roster never submits both floors during entry or projection rebuilds.
      character.setActive?.(false)
      const nameplate = document.createElement('div')
      nameplate.dataset.agentNameplate = profile.id
      nameplate.setAttribute('aria-label', `${profile.name}, project agent, ${placement.room}`)
      Object.assign(nameplate.style, {
        position: 'absolute',
        transform: 'translate(-50%, -100%)',
        minWidth: '118px',
        padding: '5px 8px 4px',
        borderRadius: '7px',
        background: 'rgba(8,12,21,0.86)',
        border: '1px solid rgba(103,232,249,0.42)',
        boxShadow: '0 5px 18px rgba(0,0,0,0.35)',
        color: '#edf8ff',
        fontFamily: 'ui-monospace, "SF Mono", monospace',
        fontSize: '11px',
        lineHeight: '1.25',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        display: 'none',
      })
      const name = document.createElement('div')
      name.textContent = profile.name
      name.style.fontWeight = '700'
      const role = document.createElement('div')
      role.textContent = `PROJECT AGENT · ${placement.room}`
      Object.assign(role.style, {
        marginTop: '2px',
        color: '#8a93a8',
        fontSize: '9px',
        letterSpacing: '0.06em',
      })
      const nameplateStatus = document.createElement('span')
      nameplateStatus.textContent = 'ON STANDBY'
      Object.assign(nameplateStatus.style, {
        display: 'block',
        marginTop: '2px',
        color: '#67e8f9',
        fontSize: '9px',
        letterSpacing: '0.06em',
      })
      nameplate.append(name, role, nameplateStatus)
      this.overlay?.element.appendChild(nameplate)
      this.entries.set(profile.id, {
        index,
        profile,
        placement,
        root,
        materials,
        visual,
        status: 'queued',
        character,
        characterActive: false,
        nameplate,
        nameplateStatus,
      })
    })
  }

  private applyStatus(entry: Entry, status: string): void {
    entry.nameplateStatus.textContent = status === 'queued'
      ? 'ON STANDBY'
      : status.toUpperCase()
    entry.nameplateStatus.style.color = status === 'failed' ? '#fca5a5' : '#67e8f9'
    if (entry.status === status) return
    const materials = this.materialsFor(status)
    bindStationVisualMaterials(entry.visual, materials)
    if (this.stationBatchGroupId !== undefined) {
      this.app.batcher?.markGroupDirty(this.stationBatchGroupId)
    }
    entry.materials = materials
    entry.status = status
  }

  private clear(): void {
    for (const entry of this.entries.values()) {
      entry.character.destroy()
      entry.root.destroy()
      entry.nameplate.remove()
    }
    this.entries.clear()
    this.roster = []
    this.activeFloorY = null
  }

  destroy(): void {
    this.unsubscribe?.()
    this.unsubscribe = undefined
    this.roomActive = false
    this.lifecycleGeneration += 1
    this.clear()
    this.sharedMaterials.clear()
    this.monitorDisplay = undefined
    if (this.stationBatchGroupId !== undefined) {
      this.app.batcher?.removeGroup(this.stationBatchGroupId)
      this.stationBatchGroupId = undefined
    }
    this.overlay?.dispose()
    this.overlay = null
    this.overflowBadge = null
  }
}
